// src/lib/emailService.js
// Brevo E-Mail Service für S&I.

import { supabase } from './supabase';
import { projectHost } from './projectUrl';
import { adminFetch } from './apiClient';
import { getPackage, calculatePricing, formatPrice } from './pricing';
import { generateContractPDF } from './contractPDF';
import { generateInvoicePDF } from './invoicePDF';
import { generateWebsiteQRSVG, encodeToModules } from './qrGenerator';

/**
 * Generate styled QR code as PNG base64 (Brevo doesn't accept SVG)
 * Uses the styled SVG generator, renders to canvas, exports as PNG
 */
function generateQRPNGBase64(url, project = {}) {
  try {
    const opts = {
      url: url.startsWith('http') ? url : `https://${url}`,
      size: 600,
      color: project.qr_color || '#0A0A0A',
      style: project.qr_style || 'square',
      logoText: project.qr_logo_type === 'text' ? (project.qr_logo_text || '') : '',
      logoImage: project.qr_logo_type === 'image' ? (project.qr_logo_image || '') : '',
      frameText: (project.qr_frame_style && project.qr_frame_style !== 'none') ? (project.qr_frame_text || '') : '',
      frameStyle: project.qr_frame_style || 'none',
    };
    
    const svg = generateWebsiteQRSVG(opts);
    if (!svg) return null;

    // Get dimensions from SVG
    const match = svg.match(/viewBox="0 0 (\d+) (\d+)"/);
    const w = match ? parseInt(match[1]) : 600;
    const h = match ? parseInt(match[2]) : 600;

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');

    // Render SVG to canvas synchronously via data URL
    const img = new Image();
    const svgBlob = new Blob([svg], { type: 'image/svg+xml' });
    const svgUrl = URL.createObjectURL(svgBlob);
    
    // For sync rendering, draw white bg first as fallback
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, w, h);

    // Use a simpler fallback: render basic QR modules directly on canvas
    const fullUrl = opts.url;
    const modules = encodeToModules(fullUrl);
    if (!modules || modules.length === 0) return null;

    const moduleCount = modules.length;
    const cellSize = w / (moduleCount + 8);
    const offset = cellSize * 4;

    ctx.fillStyle = opts.color;
    for (let row = 0; row < moduleCount; row++) {
      for (let col = 0; col < moduleCount; col++) {
        if (modules[row][col]) {
          if (opts.style === 'dots') {
            ctx.beginPath();
            ctx.arc(offset + col * cellSize + cellSize/2, offset + row * cellSize + cellSize/2, cellSize * 0.45, 0, Math.PI * 2);
            ctx.fill();
          } else {
            const r = opts.style === 'rounded' ? cellSize * 0.3 : 0;
            if (r > 0) {
              const x = offset + col * cellSize;
              const y = offset + row * cellSize;
              ctx.beginPath();
              ctx.roundRect(x, y, cellSize, cellSize, r);
              ctx.fill();
            } else {
              ctx.fillRect(offset + col * cellSize, offset + row * cellSize, cellSize + 0.5, cellSize + 0.5);
            }
          }
        }
      }
    }

    // Logo text in center
    if (opts.logoText) {
      const logoSize = w * 0.22;
      ctx.fillStyle = '#FFFFFF';
      ctx.beginPath();
      ctx.arc(w/2, h/2, logoSize/2 + logoSize * 0.12, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = opts.color;
      ctx.font = `700 ${logoSize * 0.4}px Helvetica, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(opts.logoText, w/2, h/2);
    }

    URL.revokeObjectURL(svgUrl);
    return canvas.toDataURL('image/png').split(',')[1];
  } catch (e) {
    console.warn('QR PNG generation failed:', e);
    return null;
  }
}

// E-Mail wird jetzt über Backend-API gesendet (API Key nicht im Frontend!)
const EMAIL_API_URL = '/api/send-email';

// Theme Farben
const THEME_COLORS = {
  botanical: { primary: '#1B4332', accent: '#40916C', light: '#F0FDF4' },
  editorial: { primary: '#1A1A1A', accent: '#C41E3A', light: '#FAFAFA' },
  contemporary: { primary: '#2C2C2C', accent: '#D4AF37', light: '#FFFEF5' },
  luxe: { primary: '#1C1C1C', accent: '#D4AF37', light: '#FAF8F5' },
  neon: { primary: '#0D0D0D', accent: '#FF006E', light: '#FFF0F7' },
  video: { primary: '#000000', accent: '#E50914', light: '#FFF5F5' },
};


// ============================================
// BUCHUNGSÜBERSICHT FÜR E-MAIL
// ============================================
function generateBookingHTML(project, pricing, colors) {
  const pkg = getPackage(project.package);
  
  let bookingRows = '';
  
  // Paket
  if (pricing.isCustom) {
    bookingRows += `<tr><td style="padding: 8px 0; border-bottom: 1px solid #eee;">${pkg.name}</td><td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right; font-weight: 500;">${formatPrice(pricing.total)}</td></tr>`;
  } else {
    bookingRows += `<tr><td style="padding: 8px 0; border-bottom: 1px solid #eee;">Paket ${pkg.name}</td><td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right; font-weight: 500;">${formatPrice(pricing.packagePrice)}</td></tr>`;
    
    // Add-ons: enthaltene Leistungen werden ausgewiesen, aber nicht berechnet
    (pricing.addonLines || []).forEach(line => {
      const value = line.included ? 'inklusive' : `+${formatPrice(line.price)}`;
      bookingRows += `<tr><td style="padding: 8px 0; border-bottom: 1px solid #eee;">+ ${line.name}</td><td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right;">${value}</td></tr>`;
    });

    if (pricing.customExtrasPrice > 0) {
      bookingRows += `<tr><td style="padding: 8px 0; border-bottom: 1px solid #eee;">+ Weitere Positionen</td><td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right;">+${formatPrice(pricing.customExtrasPrice)}</td></tr>`;
    }
    
    // Rabatt
    if (pricing.discount > 0) {
      bookingRows += `<tr><td style="padding: 8px 0; border-bottom: 1px solid #eee; color: #10B981;">Rabatt</td><td style="padding: 8px 0; border-bottom: 1px solid #eee; text-align: right; color: #10B981;">-${formatPrice(pricing.discount)}</td></tr>`;
    }
  }

  return `
    <div style="background: ${colors.light}; border: 2px solid ${colors.primary}; margin: 25px 0;">
      <div style="background: ${colors.primary}; color: #FFFFFF; padding: 12px 20px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; font-size: 12px;">
        Eure Buchung
      </div>
      <div style="padding: 20px;">
        <table style="width: 100%; border-collapse: collapse; font-size: 14px;">
          ${bookingRows}
          <tr>
            <td style="padding: 12px 0; font-weight: 700; font-size: 16px;">Gesamt</td>
            <td style="padding: 12px 0; text-align: right; font-weight: 700; font-size: 18px; color: ${colors.accent};">${formatPrice(pricing.total)}</td>
          </tr>
        </table>
        
        <div style="margin-top: 20px; padding-top: 15px; border-top: 1px solid #ddd; font-size: 13px; color: #666;">
          <p style="margin: 0 0 5px 0;"><strong>Zahlungsplan:</strong></p>
          <p style="margin: 0;">1. Rate (50%): ${formatPrice(pricing.total / 2)} – bei Vertragsabschluss</p>
          <p style="margin: 5px 0 0 0;">2. Rate (50%): ${formatPrice(pricing.total / 2)} – bei Go-Live</p>
        </div>
      </div>
    </div>
  `;
}

// ============================================
// E-MAIL TEMPLATE GENERATOR
// ============================================
function generateEmailHTML(type, variables, theme = 'editorial') {
  const colors = THEME_COLORS[theme] || THEME_COLORS.editorial;
  const { partner1_name, partner2_name, couple_names, wedding_date, package_name, admin_url, admin_password, website_url, new_password, project, pricing } = variables;

  const name1 = partner1_name || couple_names?.split('&')[0]?.trim() || '';
  const name2 = partner2_name || couple_names?.split('&')[1]?.trim() || '';
  const greeting = name1 && name2 ? `Liebe/r ${name1}, liebe/r ${name2}` : (name1 ? `Liebe/r ${name1}` : 'Hallo');

  const header = `
    <div style="background: #0A0A0A; padding: 40px 30px; text-align: center;">
      <div style="font-family: 'Roboto', 'Arial Black', sans-serif; font-size: 32px; font-weight: 700; color: #FFFFFF; letter-spacing: -2px;">S&I.</div>
    </div>
  `;

  const footer = `
    <div style="background: #0A0A0A; padding: 30px; text-align: center; color: rgba(255,255,255,0.7); font-size: 12px;">
      <p style="margin: 0; font-family: 'Roboto', Arial, sans-serif;">S&I. | Premium Hochzeits-Websites</p>
      <p style="margin: 8px 0 0 0;"><a href="mailto:wedding@sarahiver.de" style="color: #FFFFFF;">wedding@sarahiver.de</a></p>
    </div>
  `;

  const button = (url, text) => `
    <p style="text-align: center; margin: 30px 0;">
      <a href="${url}" style="display: inline-block; background: ${colors.accent}; color: #FFFFFF; padding: 16px 32px; text-decoration: none; font-family: 'Roboto', Arial, sans-serif; font-weight: 700; font-size: 14px; text-transform: uppercase; letter-spacing: 1px;">
        ${text}
      </a>
    </p>
  `;

  const formattedDate = wedding_date ? new Date(wedding_date).toLocaleDateString('de-DE') : 'TBD';
  const bookingHTML = project && pricing ? generateBookingHTML(project, pricing, colors) : '';

  const templates = {
    welcome: {
      subject: `Willkommen bei S&I. – ${couple_names}`,
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
        </head>
        <body style="font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 0; background: #F5F5F5;">
          <div style="max-width: 600px; margin: 0 auto; background: #FFFFFF;">
            ${header}
            <div style="padding: 40px 30px; color: #333333; line-height: 1.7;">
              <h1 style="font-size: 24px; color: ${colors.primary}; margin: 0 0 20px 0; font-weight: 700;">Herzlich Willkommen!</h1>
              <p>${greeting},</p>
              <p>vielen Dank für euer Vertrauen! Wir freuen uns sehr, euch bei der Erstellung eurer persönlichen Hochzeits-Website begleiten zu dürfen.</p>
              
              ${bookingHTML}

              <div style="background: #FFF9E6; border-left: 4px solid #F59E0B; padding: 15px 20px; margin: 25px 0;">
                <p style="margin: 0 0 8px 0; font-size: 14px;"><strong>📎 Im Anhang findet ihr:</strong></p>
                <p style="margin: 0; font-size: 14px;">• <strong>Vertrag</strong> – bitte unterschrieben per E-Mail zurücksenden</p>
                <p style="margin: 4px 0 0 0; font-size: 14px;">• <strong>Rechnung</strong> – mit Zahlungsplan und Bankverbindung</p>
                ${variables.has_qr ? '<p style="margin: 4px 0 0 0; font-size: 14px;">• <strong>QR-Code</strong> – euer persönlicher QR-Code zur Hochzeitsseite</p>' : ''}
              </div>

              <p>Bitte überweist die erste Rate (50%) innerhalb von 14 Tagen auf das in der Rechnung angegebene Konto.</p>
              <p>In einer separaten E-Mail erhaltet ihr eure Zugangsdaten zum Admin-Dashboard.</p>

              <div style="background: #FFF9F0; border: 2px solid #F59E0B; padding: 20px 24px; margin: 30px 0; border-radius: 4px;">
                <p style="margin: 0 0 6px 0; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; color: #92400E; font-weight: 700;">🎁 Exklusiv für euch</p>
                <p style="margin: 0 0 10px 0; font-size: 16px; font-weight: 700; color: #1A1A1A;">15 € Gutschein bei hochzeitsplaza.de</p>
                <p style="margin: 0 0 14px 0; font-size: 14px; color: #555; line-height: 1.6;">Als kleines Dankeschön erhaltet ihr <strong>15 € Rabatt auf Karten</strong> bei unserem Partner hochzeitsplaza.de – ohne Mindestbestellwert.</p>
                <div style="background: #0A0A0A; color: #FFFFFF; display: inline-block; padding: 10px 22px; font-family: monospace; font-size: 18px; font-weight: 700; letter-spacing: 3px; margin-bottom: 14px;">SUI15</div>
                <br>
                <a href="https://www.hochzeitsplaza.de" style="color: #D97706; font-weight: 600; font-size: 13px; text-decoration: underline;">→ Jetzt einlösen auf hochzeitsplaza.de</a>
              </div>

              <p style="margin-top: 30px;">Herzliche Grüße,<br><strong>Euer S&I. Team</strong></p>
            </div>
            ${footer}
          </div>
        </body>
        </html>
      `
    },

    credentials: {
      subject: `Eure Zugangsdaten – ${couple_names}`,
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
        </head>
        <body style="font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 0; background: #F5F5F5;">
          <div style="max-width: 600px; margin: 0 auto; background: #FFFFFF;">
            ${header}
            <div style="padding: 40px 30px; color: #333333; line-height: 1.7;">
              <h1 style="font-size: 24px; color: ${colors.primary}; margin: 0 0 20px 0; font-weight: 700;">Eure Zugangsdaten</h1>
              <p>${greeting},</p>
              <p>hier sind eure Zugangsdaten für das Admin-Dashboard:</p>
              
              <div style="background: ${colors.primary}; color: #FFFFFF; padding: 25px; margin: 25px 0; text-align: center;">
                <p style="margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.7;">Login-URL</p>
                <p style="margin: 8px 0 20px 0; font-size: 14px; font-family: monospace; word-break: break-all;">${admin_url}</p>
                <p style="margin: 0; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; opacity: 0.7;">Passwort</p>
                <p style="margin: 8px 0 0 0; font-size: 22px; font-family: monospace; font-weight: 700; letter-spacing: 2px;">${admin_password}</p>
              </div>

              <p><strong>Wichtig:</strong> Bitte bewahrt diese Daten sicher auf.</p>

              ${button(admin_url, 'Zum Dashboard →')}

              <p style="margin-top: 30px;">Herzliche Grüße,<br><strong>Euer S&I. Team</strong></p>
            </div>
            ${footer}
          </div>
        </body>
        </html>
      `
    },

    golive: {
      subject: `🎉 Eure Hochzeits-Website ist online! – ${couple_names}`,
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
        </head>
        <body style="font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 0; background: #F5F5F5;">
          <div style="max-width: 600px; margin: 0 auto; background: #FFFFFF;">
            ${header}
            <div style="padding: 40px 30px; color: #333333; line-height: 1.7;">
              <h1 style="font-size: 24px; color: ${colors.primary}; margin: 0 0 20px 0; font-weight: 700;">🎉 Eure Website ist live!</h1>
              <p>${greeting},</p>
              <p>großartige Neuigkeiten – eure Hochzeits-Website ist ab sofort online!</p>
              
              <div style="background: ${colors.light}; border-left: 4px solid ${colors.accent}; padding: 20px; margin: 25px 0; text-align: center;">
                <p style="margin: 0; font-size: 18px; font-family: monospace; font-weight: 700; color: ${colors.primary};">${website_url}</p>
              </div>

              ${button('https://' + website_url, 'Website ansehen →')}

              <p>Ihr könnt den Link jetzt an eure Gäste weitergeben!</p>
              <p>Vergesst nicht, die zweite Rate (50%) zu überweisen.</p>

              <p style="margin-top: 30px;">Wir wünschen euch eine wunderschöne Hochzeit!<br><strong>Euer S&I. Team</strong></p>
            </div>
            ${footer}
          </div>
        </body>
        </html>
      `
    },

    reminder: {
      subject: `Erinnerung: Inhalte für eure Website – ${couple_names}`,
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
        </head>
        <body style="font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 0; background: #F5F5F5;">
          <div style="max-width: 600px; margin: 0 auto; background: #FFFFFF;">
            ${header}
            <div style="padding: 40px 30px; color: #333333; line-height: 1.7;">
              <h1 style="font-size: 24px; color: ${colors.primary}; margin: 0 0 20px 0; font-weight: 700;">Freundliche Erinnerung</h1>
              <p>${greeting},</p>
              <p>wir möchten euch freundlich daran erinnern, dass wir noch auf einige Inhalte für eure Hochzeits-Website warten.</p>
              
              ${button(admin_url, 'Zum Dashboard →')}

              <p>Je früher wir alle Inhalte haben, desto schneller können wir eure Website fertigstellen!</p>

              <p style="margin-top: 30px;">Herzliche Grüße,<br><strong>Euer S&I. Team</strong></p>
            </div>
            ${footer}
          </div>
        </body>
        </html>
      `
    },

    password_reset: {
      subject: `Neues Passwort – ${couple_names}`,
      html: `
        <!DOCTYPE html>
        <html>
        <head>
          <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
        </head>
        <body style="font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 0; background: #F5F5F5;">
          <div style="max-width: 600px; margin: 0 auto; background: #FFFFFF;">
            ${header}
            <div style="padding: 40px 30px; color: #333333; line-height: 1.7;">
              <h1 style="font-size: 24px; color: ${colors.primary}; margin: 0 0 20px 0; font-weight: 700;">Neues Passwort</h1>
              <p>${greeting},</p>
              <p>hier ist euer neues Passwort für das Admin-Dashboard:</p>
              
              <div style="background: ${colors.primary}; color: #FFFFFF; padding: 30px; margin: 25px 0; text-align: center;">
                <p style="margin: 0; font-size: 28px; font-family: monospace; font-weight: 700; letter-spacing: 3px;">${new_password}</p>
              </div>

              ${button(admin_url, 'Jetzt einloggen →')}

              <p style="margin-top: 30px;">Herzliche Grüße,<br><strong>Euer S&I. Team</strong></p>
            </div>
            ${footer}
          </div>
        </body>
        </html>
      `
    }
  };

  return templates[type] || templates.welcome;
}

// Preisberechnung kommt aus lib/pricing.js — hier lag vorher eine zweite
// Kopie, die customExtras nicht kannte und deshalb andere Summen lieferte
// als die Anzeige im SuperAdmin.

// ============================================
// E-MAIL SENDEN VIA BREVO
// ============================================
export async function sendEmail({ to, toName, templateType, variables, theme, projectId, attachments = [] }) {
  try {
    const template = generateEmailHTML(templateType, variables, theme);

    // E-Mail über sichere Backend-API senden (mit Auth-Token!)
    const response = await adminFetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to,
        toName,
        subject: template.subject,
        htmlContent: template.html,
        attachments,
      }),
    });

    const result = await response.json();

    // Log speichern - OHNE sensible Daten (kein html_content, keine variables mit Passwörtern!)
    await supabase.from('email_logs').insert([{
      project_id: projectId,
      recipient_email: to,
      recipient_name: toName,
      subject: template.subject,
      template_type: templateType,
      theme: theme,
      // SICHERHEIT: Keine html_content oder variables mehr speichern!
      status: response.ok ? 'sent' : 'failed',
      error_message: response.ok ? null : (result.error || 'Unknown error'),
      brevo_message_id: result.messageId || null,
      sent_at: response.ok ? new Date().toISOString() : null,
    }]);

    return { success: response.ok, messageId: result.messageId, error: result.message };
  } catch (error) {
    console.error('Email error:', error);
    
    await supabase.from('email_logs').insert([{
      project_id: projectId,
      recipient_email: to,
      subject: `${templateType} - Fehler`,
      template_type: templateType,
      status: 'failed',
      error_message: error.message,
    }]);

    return { success: false, error: error.message };
  }
}

// ============================================
// WILLKOMMENS-E-MAILS SENDEN (MIT VERTRAG)
// ============================================
export async function sendWelcomeEmails(project) {
  // Pricing berechnen
  const pricing = calculatePricing(project);

  // Vertrag als PDF generieren (ausführliche Version)
  const contractResult = generateContractPDF(project, pricing, { returnBase64: true });

  // Rechnung als PDF generieren (mit Zahlungsplan)
  const invoiceResult = generateInvoicePDF(project, pricing, { returnBase64: true });

  const websiteUrl = projectHost(project);
  const hasQR = true; // QR-Code ist in jedem Paket enthalten

  const variables = {
    partner1_name: project.partner1_name,
    partner2_name: project.partner2_name,
    couple_names: project.couple_names,
    wedding_date: project.wedding_date,
    package_name: project.package,
    admin_url: `https://siwedding.de/${project.slug}/admin`,
    admin_password: project.admin_password,
    website_url: websiteUrl,
    project: project,
    pricing: pricing,
    has_qr: hasQR,
  };

  // Attachments: Vertrag + Rechnung + optional QR-Code
  const attachmentList = [
    { name: contractResult.filename, content: contractResult.base64 },
    { name: invoiceResult.filename, content: invoiceResult.base64 },
  ];

  if (hasQR) {
    const qrPng = generateQRPNGBase64(`https://${websiteUrl}`, project);
    if (qrPng) {
      attachmentList.push({
        name: `qr-code-${project.slug}.png`,
        content: qrPng,
      });
    }
  }

  // E-Mail 1: Willkommen + Vertrag + Rechnung + QR
  const welcome = await sendEmail({
    to: project.client_email,
    toName: project.client_name,
    templateType: 'welcome',
    variables,
    theme: project.theme,
    projectId: project.id,
    attachments: attachmentList,
  });

  // E-Mail 2: Zugangsdaten (30 Sekunden Verzögerung)
  await new Promise(resolve => setTimeout(resolve, 30000));
  
  const credentials = await sendEmail({
    to: project.client_email,
    toName: project.client_name,
    templateType: 'credentials',
    variables,
    theme: project.theme,
    projectId: project.id,
  });

  return { welcome, credentials };
}

// ============================================
// WEITERE E-MAIL FUNKTIONEN
// ============================================
export async function sendGoLiveEmail(project) {
  const websiteUrl = projectHost(project);
  const hasQR = true; // QR-Code ist in jedem Paket enthalten
  
  // QR-Code als PNG-Attachment wenn gebucht
  const attachments = [];
  if (hasQR) {
    const qrPng = generateQRPNGBase64(`https://${websiteUrl}`, project);
    if (qrPng) {
      attachments.push({
        name: `qr-code-${project.slug}.png`,
        content: qrPng,
      });
    }
  }

  return sendEmail({
    to: project.client_email,
    toName: project.client_name,
    templateType: 'golive',
    variables: {
      partner1_name: project.partner1_name,
      partner2_name: project.partner2_name,
      couple_names: project.couple_names,
      website_url: websiteUrl,
      has_qr: hasQR,
    },
    theme: project.theme,
    projectId: project.id,
    attachments: attachments.length > 0 ? attachments : undefined,
  });
}

export async function sendReminderEmail(project) {
  return sendEmail({
    to: project.client_email,
    toName: project.client_name,
    templateType: 'reminder',
    variables: {
      partner1_name: project.partner1_name,
      partner2_name: project.partner2_name,
      couple_names: project.couple_names,
      admin_url: `https://siwedding.de/${project.slug}/admin`,
    },
    theme: project.theme,
    projectId: project.id,
  });
}

export async function sendPasswordResetEmail(project, newPassword) {
  return sendEmail({
    to: project.client_email,
    toName: project.client_name,
    templateType: 'password_reset',
    variables: {
      partner1_name: project.partner1_name,
      partner2_name: project.partner2_name,
      couple_names: project.couple_names,
      new_password: newPassword,
      admin_url: `https://siwedding.de/${project.slug}/admin`,
    },
    theme: project.theme,
    projectId: project.id,
  });
}

// ============================================
// ADMIN-BENACHRICHTIGUNG: KUNDE HAT DATEN EINGEGEBEN
// ============================================
export async function sendDataReadyNotification(project) {
  const adminEmail = 'wedding@sarahiver.de';
  const timestamp = new Date().toLocaleString('de-DE', {
    dateStyle: 'long',
    timeStyle: 'short',
  });
  
  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;700&display=swap" rel="stylesheet">
    </head>
    <body style="font-family: 'Roboto', Arial, sans-serif; margin: 0; padding: 0; background: #F5F5F5;">
      <div style="max-width: 600px; margin: 0 auto; background: #FFFFFF;">
        <div style="background: #0A0A0A; padding: 30px; text-align: center;">
          <h1 style="font-family: 'Oswald', sans-serif; font-size: 24px; color: #FFFFFF; margin: 0;">
            S&I. <span style="color: #F97316;">Admin</span>
          </h1>
        </div>
        <div style="padding: 40px 30px; color: #333333; line-height: 1.7;">
          <div style="background: #FFF7ED; border-left: 4px solid #F97316; padding: 20px; margin-bottom: 25px;">
            <h2 style="font-size: 20px; color: #C2410C; margin: 0 0 10px 0;">
              🔔 Neue Daten bereit zur Prüfung
            </h2>
            <p style="margin: 0; color: #9A3412;">
              Ein Kunde hat seine Daten eingegeben und wartet auf Finalisierung.
            </p>
          </div>
          
          <table style="width: 100%; border-collapse: collapse; margin-bottom: 25px;">
            <tr>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5; font-weight: 700; width: 140px;">Projekt:</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5;">${project.couple_names || project.slug}</td>
            </tr>
            <tr>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5; font-weight: 700;">Kunde:</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5;">${project.client_name || '–'}</td>
            </tr>
            <tr>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5; font-weight: 700;">E-Mail:</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5;">${project.client_email || '–'}</td>
            </tr>
            <tr>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5; font-weight: 700;">Hochzeitsdatum:</td>
              <td style="padding: 10px 0; border-bottom: 1px solid #E5E5E5;">${project.wedding_date ? new Date(project.wedding_date).toLocaleDateString('de-DE') : '–'}</td>
            </tr>
            <tr>
              <td style="padding: 10px 0; font-weight: 700;">Zeitstempel:</td>
              <td style="padding: 10px 0;">${timestamp}</td>
            </tr>
          </table>
          
          <a href="https://superadmin.siwedding.de/projects/${project.id}" 
             style="display: block; background: #F97316; color: #FFFFFF; padding: 15px 30px; 
                    text-align: center; text-decoration: none; font-weight: 700; margin: 20px 0;">
            Zum Projekt im SuperAdmin →
          </a>
          
          <p style="font-size: 0.85rem; color: #666666; margin-top: 30px;">
            Der Status wurde automatisch auf "Bereit zur Prüfung" gesetzt.
          </p>
        </div>
        <div style="background: #0A0A0A; padding: 20px; text-align: center;">
          <p style="font-size: 0.75rem; color: rgba(255,255,255,0.5); margin: 0;">
            S&I. SuperAdmin Notification
          </p>
        </div>
      </div>
    </body>
    </html>
  `;
  
  const subject = `🔔 Daten bereit: ${project.couple_names || project.slug}`;

  try {
    // E-Mail über sichere Backend-API senden (mit Auth-Token!)
    const response = await adminFetch(EMAIL_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: adminEmail,
        toName: 'Iver',
        subject,
        htmlContent,
      }),
    });

    const result = await response.json();

    // Log speichern - OHNE sensible Daten
    await supabase.from('email_logs').insert([{
      project_id: project.id,
      recipient_email: adminEmail,
      recipient_name: 'Admin',
      subject,
      template_type: 'admin_data_ready',
      theme: project.theme,
      status: response.ok ? 'sent' : 'failed',
      error_message: response.ok ? null : (result.error || 'Unknown error'),
      brevo_message_id: result.messageId || null,
      sent_at: response.ok ? new Date().toISOString() : null,
    }]);

    return { success: response.ok, messageId: result.messageId, error: result.error };
  } catch (error) {
    console.error('Admin notification error:', error.message);

    await supabase.from('email_logs').insert([{
      project_id: project.id,
      recipient_email: adminEmail,
      subject,
      template_type: 'admin_data_ready',
      status: 'failed',
      error_message: error.message,
    }]);

    return { success: false, error: error.message };
  }
}

export default { sendEmail, sendWelcomeEmails, sendGoLiveEmail, sendReminderEmail, sendPasswordResetEmail, sendDataReadyNotification };
